// REALISTIC EUROPEAN DRAGON (2026-11-04, owner: realistic models, CC0/CC-BY
// only, "modify a bit the 3D model … my game should be unique").
// A rigged, animated dragon — "European Dragon" by Regina Cachoa, CC-BY 4.0 —
// reworked by agent/dragon-asset/pipeline.mjs (neutral tintable hide with our
// own dorsal bands, new vein-patterned membrane, glowing amber eyes, ivory
// teeth, one flight clip). See public/models/dragon/CREDITS.md and
// assetCredits.ts.
//
// Replaces the VISUAL of the procedural buildEuropeanDragon (ambientLife.ts)
// on both live european dragons: the three ambient dragons circling
// medieval_castle (AmbientLife) and the rideable one (match.ts euroRide). The
// caller keeps the procedural rig's group, transforms and flight model; a
// DragonModel is added INTO that group (fitted to the procedural dragon's
// local frame: forward −Z, chest at the origin, same length) and the
// procedural parts are hidden. The procedural rig stays the fallback.
//
// Contract (same as horseModel.ts / zombieModel.ts):
// - Armed ONLY by warmDragonModel() under the match load screen, after one
//   real frame through the game's render path compiled its programs (colour +
//   skinned shadow depth) and uploaded its textures. Not loaded by then → the
//   round keeps the procedural dragons; they never pop in mid-fight.
// - Geometry/textures are shared (cached GLB); the HIDE material is cloned per
//   dragon so each carries its own palette tint — a uniform, so every tint hits
//   the same compiled program.
// - No per-frame allocation: update() writes weights/timeScales on
//   preallocated actions and calls mixer.update once.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfWithin, instantiate, loadGltf, warmStageNow, type WarmStage } from "./gltfAssets.js";

export const DRAGON_GLB_URL = "/models/dragon/dragon.glb";

/** Fit target, in the procedural dragon's local units (group scale 1): snout
 *  (z −4.8) to tail tip (≈ +7) measured on the glide pose, and where its chest
 *  sits (the torso ball at z −0.9). The callers' group scales (2.6 ridden,
 *  3.0–4.0 ambient) then give the same animal as before. */
const TARGET_LEN = 11.5;
/** …and its wingspan (≈ 10.4 at scale 1, tip to tip). The source is a
 *  short-bodied, broad-winged animal, so whichever of the two binds first
 *  sets the scale (in practice the span) — it never outgrows the old one. */
const TARGET_SPAN = 11;
const CHEST_Z = -0.9;
/** The source's chest + hip bones (three sanitises the dots out of names). */
const CHEST_BONE = "DEF-Spine004";
const HIP_BONE = "DEF-Spine_02";
/** The source flight is a HOVER — the body stands ~47° nose-up. The fit
 *  levels it so the hip→chest line rises only this much: forward flight. */
const BODY_PITCH = 0.14;
/** The `fly` clip (trimmed + retimed by the pipeline) holds two wingbeats in
 *  1.974 s. GLIDE_T is the frame where the wings pass just above level at full
 *  span on the downstroke — frozen, it is the soaring/gliding pose (measured
 *  offline: wingtip ≈ shoulder height + a slight dihedral, span at its max). */
const GLIDE_T = 1.224;
/** The clip's native beat (rad/s): 2 beats / 1.974 s. Callers pass the beat
 *  rate they used to drive the procedural wings; timeScale = theirs / this. */
const CLIP_BEAT = (2 * Math.PI * 2) / 1.974;
/** The hide texture is a mid-grey detail layer; the procedural palettes are
 *  dark toon colours, so the tint is lifted to land at a similar brightness
 *  under the PBR lights. */
const TINT_GAIN = 2.1;

let template: GLTF | null = null;
let flyClip: THREE.AnimationClip | null = null;
let glideClip: THREE.AnimationClip | null = null;
let fitScale = 1;
/** Source-space offset that puts the chest bone at the tilt pivot. */
const fitOffset = new THREE.Vector3();
/** Rotation about source X that levels the hovering body (positive = nose down here). */
let fitTilt = 0;
let armed = false;
let armedFor: object | null = null;

export function dragonModelArmed(): boolean { return armed && template !== null; }
export function disarmDragonModel(): void { armed = false; armedFor = null; }

function prepare(g: GLTF | null): GLTF | null {
  if (!g || template) return template ?? g;
  const fly = THREE.AnimationClip.findByName(g.animations, "fly");
  if (!fly) { console.error("[dragon] fly clip missing"); return null; }
  // measure on the GLIDE pose (the bind pose is a ground stance with the tail
  // and wings folded — not the silhouette the game shows). One throwaway
  // instance, once per session.
  const probe = instantiate(g);
  const tilt = new THREE.Group();
  tilt.add(probe);
  const mixer = new THREE.AnimationMixer(probe);
  const a = mixer.clipAction(fly);
  a.play();
  a.time = GLIDE_T;
  mixer.update(0);
  tilt.updateMatrixWorld(true);
  const find = (prefix: string): THREE.Object3D | undefined => {
    let f: THREE.Object3D | undefined;
    probe.traverse((o) => { if (!f && o.name.startsWith(prefix)) f = o; });
    return f;
  };
  const chest = find(CHEST_BONE), hip = find(HIP_BONE);
  const ok = chest !== undefined && hip !== undefined;
  const c = new THREE.Vector3(), h = new THREE.Vector3();
  if (chest && hip) { chest.getWorldPosition(c); hip.getWorldPosition(h); }
  // level the hover: the source faces +Z, so its nose-up angle is atan2(dy, dz)
  // of hip→chest; a +X rotation by θ turns that angle into (angle − θ) in the
  // Y/Z plane, so θ = angle − BODY_PITCH lowers the nose to BODY_PITCH
  fitTilt = Math.atan2(c.y - h.y, c.z - h.z) - BODY_PITCH;
  probe.position.set(-c.x, -c.y, -c.z); // chest at the pivot
  tilt.rotation.x = fitTilt;
  tilt.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(tilt, true); // skins the vertices
  const size = box.getSize(new THREE.Vector3());
  mixer.stopAllAction();
  mixer.uncacheRoot(probe);
  probe.traverse((o) => { const m = o as THREE.SkinnedMesh; if (m.isSkinnedMesh) m.skeleton.dispose(); });
  if (!(size.z > 0) || !(size.x > 0) || !Number.isFinite(size.z) || !ok || !Number.isFinite(fitTilt)) { console.error("[dragon] unexpected GLB bounds/rig"); return null; }
  // the procedural dragon faces −Z: the fit group turns the levelled source
  // 180° about Y, scales it, and sits the chest at (0, 0, CHEST_Z)
  fitScale = Math.min(TARGET_LEN / size.z, TARGET_SPAN / size.x);
  fitOffset.set(-c.x, -c.y, -c.z);
  let hide = false;
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    // bounding sphere once at bind pose, padded: the flapping wings reach far
    // past the folded bind-pose silhouette
    m.computeBoundingSphere();
    if (m.boundingSphere) m.boundingSphere.radius *= 1.8;
    m.castShadow = true;
    m.receiveShadow = true;
    if ((m.material as THREE.Material).name === "DragonHide") hide = true;
  });
  if (!hide) { console.error("[dragon] hide material not found"); return null; }
  flyClip = fly;
  // a second clip object so the frozen glide frame gets its own action
  glideClip = fly.clone();
  glideClip.name = "glide";
  template = g;
  console.log(`[dragon] model ready: glide pose ${size.x.toFixed(2)}×${size.y.toFixed(2)}×${size.z.toFixed(2)} → scale ${fitScale.toFixed(3)}, tilt ${fitTilt.toFixed(2)}`);
  return g;
}

/** Start the fetch early (match start). Safe to call repeatedly. */
export function preloadDragonModel(): void {
  void loadGltf(DRAGON_GLB_URL).then(prepare);
}

export class DragonModel {
  /** Container in the procedural dragon group's local space. */
  readonly root = new THREE.Group();
  private readonly fit = new THREE.Group();
  private readonly tilt = new THREE.Group();
  private readonly inner: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly fly: THREE.AnimationAction;
  private readonly glide: THREE.AnimationAction;
  private readonly skinned: THREE.SkinnedMesh[] = [];
  /** Per-dragon hide (cloned so the palette tint is per instance). */
  readonly hide: THREE.MeshStandardMaterial;
  private flapW = 1;

  constructor(tint: THREE.ColorRepresentation) {
    const g = template!;
    this.inner = instantiate(g);
    this.fit.rotation.y = Math.PI;
    this.fit.scale.setScalar(fitScale);
    this.fit.position.set(0, 0, CHEST_Z);
    this.tilt.rotation.x = fitTilt;
    this.inner.position.copy(fitOffset);
    this.tilt.add(this.inner);
    this.fit.add(this.tilt);
    this.root.add(this.fit);
    this.root.name = "npDragonModel";
    let hide: THREE.MeshStandardMaterial | null = null;
    this.inner.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh) return;
      const mat = m.material as THREE.MeshStandardMaterial;
      if (mat.name === "DragonHide") {
        if (!hide) { hide = mat.clone(); hide.userData.shared = false; }
        m.material = hide;
      }
      this.skinned.push(m);
    });
    this.hide = hide!;
    this.setTint(tint);
    this.mixer = new THREE.AnimationMixer(this.inner);
    this.fly = this.mixer.clipAction(flyClip!);
    this.fly.play();
    this.fly.time = Math.random() * flyClip!.duration; // a flight never beats in lockstep
    this.glide = this.mixer.clipAction(glideClip!);
    this.glide.play();
    this.glide.time = GLIDE_T;
    this.glide.paused = true;
    this.glide.setEffectiveWeight(0);
    this.fly.setEffectiveWeight(1);
    this.mixer.update(0);
  }

  setTint(c: THREE.ColorRepresentation): void {
    this.hide.color.set(c).multiplyScalar(TINT_GAIN);
  }

  /** Once per rendered frame. `flap` 0..1 = powered flight vs held glide;
   *  `beat` = the wingbeat rate (rad/s) the caller wants. */
  update(dt: number, flap: number, beat: number): void {
    const f = Math.max(0, Math.min(1, flap));
    this.flapW += (f - this.flapW) * (1 - Math.exp(-dt * 5));
    this.fly.setEffectiveWeight(this.flapW);
    this.glide.setEffectiveWeight(1 - this.flapW);
    this.fly.timeScale = Math.max(0.25, Math.min(2.5, beat / CLIP_BEAT));
    this.mixer.update(dt);
  }

  skinnedMeshes(): readonly THREE.SkinnedMesh[] { return this.skinned; }

  /** Free the per-instance pieces: bone textures, mixer bindings, the hide. */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.inner);
    for (const m of this.skinned) m.skeleton.dispose();
    this.hide.dispose();
    this.root.removeFromParent();
  }
}

/** Swap a procedural dragon rig's visual for the model: hide every procedural
 *  part of `group` and add a DragonModel to it. The rig's wing groups keep
 *  being driven by the caller (harmless — hidden). */
export function mountDragonModel(group: THREE.Group, tint: THREE.ColorRepresentation): DragonModel {
  for (const c of group.children) c.visible = false;
  const m = new DragonModel(tint);
  group.add(m.root);
  return m;
}

interface WarmTarget { readonly scene: THREE.Scene; readonly camera: THREE.Camera; render(): void }

/** Under the match load screen: wait (bounded) for the GLB, render ONE real
 *  frame with a dragon in front of the camera (compiles the skinned colour +
 *  shadow programs, uploads textures), then arm. Caller gates the TV app. */
export async function warmDragonModel(r: WarmTarget, timeoutMs = 3000): Promise<boolean> {
  return warmStageNow(r, await stageDragonWarm(r, timeoutMs));
}

/** Batched form (gltfAssets WarmStage) — see horseModel.stageHorseWarm. */
export async function stageDragonWarm(r: WarmTarget, timeoutMs = 3000): Promise<WarmStage | null> {
  if (armed && armedFor === r) return { root: null, arm() { /* already warm */ }, dispose() { /* nothing staged */ } };
  armed = false;
  armedFor = null;
  const g = await gltfWithin(loadGltf(DRAGON_GLB_URL).then(prepare), timeoutMs);
  if (!g || !template) { console.log("[dragon] model not ready at the load-screen deadline — procedural dragons this round"); return null; }
  const d = new DragonModel("#5a3a22");
  const cam = r.camera;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  d.root.position.copy(cam.position).addScaledVector(fwd, 12);
  d.root.scale.setScalar(0.5);
  for (const m of d.skinnedMeshes()) m.frustumCulled = false;
  d.update(1 / 60, 1, CLIP_BEAT);
  return { root: d.root, arm: () => { armed = true; armedFor = r; }, dispose: () => d.dispose() };
}
